import type { EditProps, RaRecord } from "react-admin";
import {
  Edit,
  SimpleForm,
  TextInput,
  ReferenceInput,
  AutocompleteInput,
  FormDataConsumer,
  Toolbar,
  SaveButton,
  DeleteButton,
} from "react-admin";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { formatListDate, formatMajlisStartTimeLabel } from "@/utils/date-format";

function trimStr(v: unknown): string {
  if (v == null) return "";
  return String(v).trim();
}

/** Compact label for `raza-events` autocomplete: date, time, purpose, venue/hall. */
function formatLagatRazaEventOptionText(r: RaRecord): string {
  const datePart = r.date ? formatListDate(String(r.date), { empty: "" }) : "";
  const startRaw = trimStr(r.startTime);
  const timePart = startRaw ? formatMajlisStartTimeLabel(startRaw) : "";

  const title = trimStr(r.title);
  const ohbatType = trimStr(r.ohbatType);
  const purposeName = trimStr(r.purposeName);
  const razaRequest = r.razaRequest as { purpose?: string | null } | null | undefined;
  const requestPurpose = trimStr(razaRequest?.purpose);
  const purpose = title || purposeName || requestPurpose || ohbatType;

  const hallsRaw = r.hallBookings as Array<{ hall?: { name?: string | null } | null }> | undefined;
  const hallNames = [
    ...new Set((hallsRaw ?? []).map((hb) => trimStr(hb.hall?.name)).filter((n) => n.length > 0)),
  ];
  const hallPart = hallNames.join(", ");
  const address = trimStr(r.address);
  const venue = [hallPart || null, address || null].filter(Boolean).join(" · ");

  return (
    [datePart, timePart, purpose || null, venue || null].filter(Boolean).join(" · ") || String(r.id)
  );
}

const transformPayload = (data: Record<string, unknown>) => {
  const nullIfEmpty = (v: unknown) => (v === "" || v === undefined ? null : v === null ? null : v);

  const razaRequestId = nullIfEmpty(data.razaRequestId);
  let razaEventId = nullIfEmpty(data.razaEventId);
  if (razaRequestId === null || razaRequestId === "") {
    razaEventId = null;
  }

  return {
    ...data,
    bookingId: nullIfEmpty(data.bookingId),
    razaRequestId,
    razaEventId,
  };
};

const LagatReceiptEditToolbar = () => (
  <Toolbar sx={{ display: "flex", gap: 1, justifyContent: "space-between", flexWrap: "wrap" }}>
    <SaveButton />
    <DeleteButton redirect="list" />
  </Toolbar>
);

const LagatReceiptEdit = (props: EditProps) => (
  <Edit {...props} mutationMode="pessimistic" transform={transformPayload}>
    <SimpleForm toolbar={<LagatReceiptEditToolbar />} warnWhenUnsavedChanges sx={{ maxWidth: 720 }}>
      <Typography variant="subtitle2" color="text.secondary">
        Receipt
      </Typography>
      <TextInput source="receiptNo" label="Receipt no." disabled fullWidth />
      <TextInput source="name" label="Name" fullWidth />
      <TextInput source="itsNo" label="ITS no." fullWidth />

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Manual links
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Tie this lagat to a hall booking, raza request, and/or event when records were created
        separately. Leave fields empty to remove a link.
      </Typography>

      <ReferenceInput source="bookingId" reference="bookings" allowEmpty perPage={100}>
        <AutocompleteInput
          label="Hall booking"
          fullWidth
          optionText={(r: RaRecord) =>
            [r.bookingNo, r.organiser, r.itsNo].filter(Boolean).join(" — ") || String(r.id)
          }
        />
      </ReferenceInput>

      <ReferenceInput source="razaRequestId" reference="razaRequests" allowEmpty perPage={100}>
        <AutocompleteInput
          label="Raza request"
          fullWidth
          optionText={(r: RaRecord) =>
            [r.name, r.itsNo].filter(Boolean).join(" — ") || String(r.id)
          }
        />
      </ReferenceInput>

      <FormDataConsumer>
        {({ formData }) =>
          formData?.razaRequestId ? (
            <ReferenceInput
              source="razaEventId"
              reference="raza-events"
              allowEmpty
              perPage={100}
              filter={{ razaRequestId: String(formData.razaRequestId) }}
            >
              <AutocompleteInput
                label="Event"
                fullWidth
                helperText="Events for this raza request only"
                optionText={(r: RaRecord) => formatLagatRazaEventOptionText(r)}
              />
            </ReferenceInput>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Choose a raza request above to link an event.
            </Typography>
          )
        }
      </FormDataConsumer>
    </SimpleForm>
  </Edit>
);

export default LagatReceiptEdit;
